import React, { type ReactNode, useCallback, useContext, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import type { ExternalAgentInvokedBy } from '@atlassian/agent-profile-card/external-card';

import { CardCloseGuardContext } from '../../store/cardCloseGuard';
import { UserProfileCardWithTrigger } from '../UserProfileCardWithTrigger';

const messages = defineMessages({
	viewInvokerProfile: {
		id: 'user-profile-card.InvokingUserProfileCardTrigger.viewInvokerProfile',
		defaultMessage: 'More information about {name}',
		description: 'Accessible label for an invoking person on an external agent profile card.',
	},
});

export const InvokingUserProfileCardTrigger = ({
	user,
	cloudId,
	orgId,
	viewerId,
	visibleLabel,
	children,
}: {
	user: ExternalAgentInvokedBy & { accountId: string };
	cloudId: string;
	orgId?: string;
	viewerId?: string;
	visibleLabel?: string;
	children: ReactNode;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const parentCloseGuard = useContext(CardCloseGuardContext);
	const holdParentCardOpen = useCallback(
		(held: boolean) => {
			if (parentCloseGuard) {
				parentCloseGuard.current = held;
			}
		},
		[parentCloseGuard],
	);
	useEffect(() => () => holdParentCardOpen(false), [holdParentCardOpen]);

	return (
		<UserProfileCardWithTrigger
			userId={user.accountId}
			cloudId={cloudId}
			orgId={orgId}
			viewerId={viewerId}
			trigger="click"
			placement="right-start"
			offset={[0, 8]}
			prefilledProfileData={{
				accountId: user.accountId,
				name: user.name,
				avatarUrl: user.avatarUrl,
			}}
			ariaLabel={visibleLabel ?? formatMessage(messages.viewInvokerProfile, { name: user.name })}
			onOpen={() => holdParentCardOpen(true)}
			onClose={() => holdParentCardOpen(false)}
		>
			{children}
		</UserProfileCardWithTrigger>
	);
};
